<script setup lang="ts">
import { PropType, computed, inject, nextTick, onMounted, ref } from 'vue'
import {
  ExtendedHeading,
  IElement,
  PositionMapping,
  positionMarkerPluginKey,
  PositionMarkerExtension,
  delay,
  CustomLineHeight,
} from '@gohighlevel/ghl-proposals-common'
import CWrapper from './CWrapper.vue'
import {
  Color,
  LineHeight,
  Link,
  RequiredExtensions,
  TextAlign,
  TextBlocks,
  TextSize,
  UIRichTextEditor,
} from '@gohighlevel/ghl-text-editor'
import { Editor } from '@tiptap/vue-3'
import FontFamily from '@tiptap/extension-font-family'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import BulletList from '@tiptap/extension-bullet-list'
import StarterKit from '@tiptap/starter-kit'
import ListItem from '@tiptap/extension-list-item'
import HardBreak from '@tiptap/extension-hard-break'
import { useBlockElements, useDocument } from '@/composable'
import { useMarkerForEditor } from '@/composable/useMarkerForEditor'
import { useFillableFieldsV2 } from '@/composable/useFillableFieldsV2'

import { useRoute } from 'vue-router'

const props = defineProps({
  element: {
    type: Object as PropType<IElement>,
    required: true,
  },
})
const isPDFView = inject<boolean>('isPDFView')
const { documentVersion } = useDocument()
const editor = ref()
const route = useRoute()
const isTextBlockFullyLoaded = ref(false)
const fillablePositionsRef = ref<PositionMapping>({})
const debugMode = computed(() => {
  return route.query.debug === 'true'
})
const {
  getPosAtCoordsForFillables,
  insertElementsInsideEmptyMarkers,
  isPrintAdjustToConsider,
} = useMarkerForEditor()
const { getElementRefById } = useFillableFieldsV2()
const { addBlockElement } = useBlockElements()

onMounted(() => {
  nextTick(() => {
    addBlockElement(props.element.id, props.element.type)
  })
})

const CustomBulletList = BulletList.extend({
  addCommands() {
    return {
      ...this.parent?.(),
      toggleCustomBulletList: () => editor => {
        return editor.commands.toggleBulletList()
      },
    }
  },
})

const HardBreakExtension = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (
          this.editor.isActive('orderedList') ||
          this.editor.isActive('bulletList')
        ) {
          return this.editor.chain().createParagraphNear().run()
        }
        return this.editor.commands.setHardBreak()
      },
    }
  },
})

const customStyleForTextParent = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return {
    backgroundColor: styles?.backgroundColor,
    marginTop: styles?.marginTop,
    marginBottom: styles?.marginBottom,
    marginLeft: styles?.marginLeft,
    marginRight: styles?.marginRight,
    paddingTop: styles?.paddingTop,
    paddingBottom: styles?.paddingBottom,
    paddingLeft: styles?.paddingLeft,
    paddingRight: styles?.paddingRight,
  }
})

const isDocumentVersion1 = computed(() => {
  return documentVersion.value === 1
})

const isVersionGreaterThanOne = computed(() => {
  return documentVersion.value > 1
})

const paintMarkersInPositions = async (positions: PositionMapping) => {
  if (!editor.value) return
  // Update positions through plugin
  const tr = editor.value.view.state.tr.setMeta(
    positionMarkerPluginKey,
    positions
  )
  editor.value.view.dispatch(tr)
}

const getEditorContent = (text: string) => {
  if (
    isPDFView &&
    (documentVersion.value ?? 1) > 1 &&
    !isPrintAdjustToConsider
  ) {
    // Use regex to find <br> tags that are not inside semantic content tags
    return text
      .replace(/<br>/g, '<strong><br>\u200B</strong>')
      .replace(
        /<(?!\/?(strong|em|b|i|u|a|h[1-6]|p)[^>]*>)br>/g,
        '<strong><br>\u200B</strong>'
      )
  }
  return text
}

editor.value = new Editor({
  content: getEditorContent(props.element.component.options.text),
  editable: false,
  parseOptions: {
    preserveWhitespace: 'full',
  },
  extensions: [
    StarterKit.configure({
      paragraph: {
        HTMLAttributes: {
          class: 'break-inside-auto', // Your custom class name
        },
      },
    }),
    ...((documentVersion.value ?? 1) > 1 && isPDFView
      ? [
          PositionMarkerExtension.configure({
            debugMode: debugMode.value,
            getElementRefById: getElementRefById,
            isPrintAdjustToConsider: isPrintAdjustToConsider,
          }),
        ]
      : []),
    TextSize,
    RequiredExtensions,
    TextStyle,
    FontFamily,
    TextBlocks,
    ...((documentVersion.value ?? 1) > 1 ? [ExtendedHeading] : []), // don't change the order, this should be added after TextBlocks
    Color,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      protocols: ['ftp', 'mailto', '#popup', 'sms', 'tel', '{{', '/'],
      autolink: false,
      linkOnPaste: false,
    }),
    ...((documentVersion.value ?? 1) > 1
      ? [
          CustomLineHeight.configure({
            defaultLineHeight: 1.5,
            types: ['paragraph', 'heading', 'blockquote', 'listItem'],
          }),
        ]
      : []),
    ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
    ListItem,
    CustomBulletList,
    ...((documentVersion.value ?? 1) === 1
      ? [
          HardBreakExtension.configure({
            HTMLAttributes: {
              class: 'break-after-auto page-break',
            },
          }),
        ]
      : []),
  ],
  editorProps: {
    attributes: state => {
      return {
        ...state.doc.attrs,
        ...(documentVersion.value === 1
          ? { style: 'font-family: sans-serif' }
          : {}),
        ...(documentVersion.value > 1
          ? { style: 'font-family: Open Sans' }
          : {}),
      }
    },
  },
})

/**
 * Transaction is triggered when the editor is updated, there are two updates we are focusing on
 * 1. When the editor is fully loaded, we need to calculate the markers and draw them
 * 2. When the markers have been added, we need to insert the elements inside the markers
 */
editor.value.on('transaction', async ({ transaction }) => {
  await handleTransactionForMarkerPlugin(transaction)
})

const calculateAndDrawMarkers = async () => {
  if (isTextBlockFullyLoaded.value) return
  await delay(500)
  const fillablePositions = getPosAtCoordsForFillables(editor, props.element)
  if (Object.keys(fillablePositions).length > 0) {
    fillablePositionsRef.value = fillablePositions
    isTextBlockFullyLoaded.value = true
    paintMarkersInPositions(fillablePositionsRef.value)
  }
}

const handleTransactionForMarkerPlugin = async (transaction: any) => {
  if (
    isPDFView &&
    (documentVersion.value ?? 1) > 1 &&
    !isPrintAdjustToConsider
  ) {
    //Block: To check if text block is fully loaded
    await calculateAndDrawMarkers()

    //Only triggered in case decorations have been created and added to the editor
    const editorView = editor.value?.view
    if (editorView) {
      const pluginMeta = transaction.getMeta(positionMarkerPluginKey)
      if (pluginMeta && isTextBlockFullyLoaded.value) {
        insertElementsInsideEmptyMarkers(
          fillablePositionsRef.value,
          debugMode.value
        )
      }
    }
  }
}
</script>

<template>
  <CWrapper class="flex flex-col w-full print:block">
    <div
      :style="customStyleForTextParent"
      :class="{
        'document-version-eq-1': isDocumentVersion1,
        'document-version-gt-1': isVersionGreaterThanOne,
        'pdf-view': isPDFView,
      }"
    >
      <UIRichTextEditor :editor="editor" />
    </div>
  </CWrapper>
</template>

<style lang="scss" scoped>
.editor-container {
  position: unset;
  border-radius: 0;
  border: 0px solid #d0d5dd !important;
  padding: 0px !important;
  height: 100%;
  min-width: unset;
  width: 100%;
}

.pdf-view {
  .tiptap.ProseMirror {
    overflow: visible !important;
  }
}

.editor-wrapper {
  .tiptap.ProseMirror[contenteditable='false'] {
    white-space: break-spaces !important;
  }
  p {
    break-inside: auto !important;
    font-size: 16px;
  }
}
</style>

<style lang="scss">
.editor-wrapper {
  p {
    font-size: 16px;
  }
}

.tiptap.ProseMirror {
  overflow: visible !important;
}

.document-version-eq-1 {
  .editor-wrapper {
    font-family: sans-serif;

    p {
      font-family: sans-serif;
    }
  }
}

.document-version-gt-1 {
  .editor-wrapper {
    font-weight: 400;
    text-rendering: optimizeLegibility;
    line-height: 1.5;
    font-size: 16px;
    word-break: break-word;
    white-space: pre-wrap;
    /* newer spec: preserves every space + breaks at them */
    white-space: break-spaces !important;
    font-variant-ligatures: none;
    p {
      margin: 0;
      &[style*='text-align: justify'] {
        text-align-last: left;
      }
      &[style*='text-align: left'] {
        text-align-last: left;
      }
      &[style*='text-align: right'] {
        text-align-last: right;
      }
      &[style*='text-align: center'] {
        text-align-last: center;
      }

      span {
        /* fallback for older browsers */
        white-space: pre-wrap;
        /* newer spec: preserves every space + breaks at them */
        white-space: break-spaces !important;
        white-space-collapse: preserve; /* Chrome's way of collapsing normal spaces */
        text-wrap-mode: wrap; /* Chrome-only */
        -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
        max-width: 100%;
        word-break: break-word;
        display: inline;
        vertical-align: top;
      }

      a {
        display: inline;
      }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0;
      span {
        /* keep your pre-wrap; then explicitly restore "break at spaces" */
        white-space: pre-wrap;
        /* newer spec: preserves every space + breaks at them */
        white-space: break-spaces !important;
        white-space-collapse: preserve; /* Chrome's way of collapsing normal spaces */
        text-wrap-mode: wrap; /* Chrome-only */
        -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
        max-width: 100%;
        word-break: break-word;
        display: inline;
        vertical-align: top;
      }

      a {
        display: inline;
      }
    }
  }
}
</style>
